import type { KibTagThemes } from '@chewy/kib-tag-react'
import type { Constant } from '@pbt/pbt-ui-components'
import { DateUtils } from '@pbt/pbt-ui-components'

import { RxType } from '~/api/graphql/generated/types'
import { getHighValue } from '~/components/dashboard/invoices/invoiceUtils'
import { ColorVariant, ColorVariantToTheme } from '~/constants/colors'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import i18n from '~/locales/i18n'
import type { Prescription } from '~/types/entities/prescriptions'
import {
  getPrescriptionAutoshipFrequency,
  getPrescriptionAutoshipUnitId,
} from '~/utils/prescription'
import { getRemainingRefillsCount } from '~/utils/prescriptionUtils'

export type DisplayField = {
  boldValue?: boolean
  label: string
  onClick?: () => void
  parentPrescription?: Prescription
  prescription?: Prescription
  relativeTime?: string | null
  tooltipText?: string
  value: string
}

export interface PrescriptionFlags {
  isCanceled: boolean
  isChewy: boolean
  isDeclined: boolean
  isPendingVetApproval: boolean
  isScriptOut: boolean
  stateName: string | null
}

export interface GetRxFieldsOptions {
  autoshipUnit?: Constant[]
  onDispenseRefill?: () => void
  prescriptionProductSizeUnit?: Constant[]
  prescriptionStates?: Constant[]
  productSizeUnit?: Constant[]
}

export const getPrescriptionFlags = (
  rx: Prescription,
  prescriptionType: PrescriptionType | null | undefined,
  prescriptionStates?: Constant[],
): PrescriptionFlags => {
  const stateName =
    rx.stateId && prescriptionStates
      ? (prescriptionStates.find((s) => s.id === rx.stateId)?.name ?? null)
      : null

  const isDeclined = stateName === PrescriptionStates.DECLINED
  const isCanceled = stateName === PrescriptionStates.CANCELED
  const isPendingVetApproval =
    stateName === PrescriptionStates.PENDING_VET_APPROVAL

  return {
    stateName,
    isDeclined,
    isCanceled,
    isPendingVetApproval,
    isScriptOut: prescriptionType === PrescriptionType.SCRIPT_OUT,
    isChewy: prescriptionType === PrescriptionType.CHEWY,
  }
}

const DateFormatters = {
  date: (value: string | null | undefined): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY'),
  dateTime: (value: string | null | undefined): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY | h:mmA'),
}

const PrescriptionFields = {
  expirationDate: (rx: Prescription): string | null | undefined => {
    // Check for drugExpirationDate which exists in GraphQL but may not be in TypeScript type
    const drugExpirationDate =
      'drugExpirationDate' in rx
        ? (rx as { drugExpirationDate?: string }).drugExpirationDate
        : undefined
    return (
      rx.quantityDrugExpirationDate ??
      drugExpirationDate ??
      rx.refillExpirationDate ??
      null
    )
  },

  lastFillDate: (rx: Prescription): string | null => {
    if (rx.refills?.length) {
      const mostRecent = rx.refills.reduce((latest, current) => {
        if (!latest?.creationDate) return current
        if (!current?.creationDate) return latest
        return current.creationDate > latest.creationDate ? current : latest
      }, rx.refills[0])
      return mostRecent?.creationDate ?? null
    }
    return null
  },

  numberOfRefills: (rx: Prescription): number | null =>
    rx.quantityNumbRefills ?? null,

  refillsRemaining: (rx: Prescription): number => getRemainingRefillsCount(rx),
}

const toStringOrZero = (value: number | null | undefined): string =>
  value === null || value === undefined ? '0' : String(value)

const AUTOSHIP_UNIT_WEEK = 'Week'
const AUTOSHIP_UNIT_MONTH = 'Month'

export const formatSupplyDuration = (
  autoshipFrequency: number | undefined,
  autoshipUnitName: string | undefined,
): string | null => {
  if (!autoshipFrequency || !autoshipUnitName) return null

  if (autoshipUnitName === AUTOSHIP_UNIT_MONTH) {
    return `${autoshipFrequency} ${i18n.t('Constants:TIME_UNITS.MONTH_SHORT')}.`
  }

  if (autoshipUnitName === AUTOSHIP_UNIT_WEEK) {
    return `${autoshipFrequency}-${i18n.t('Constants:TIME_UNITS.WEEK_SHORT')}s.`
  }

  return null
}

/**
 * Gets the supply duration string for a prescription.
 * Returns formatted string like "6 mo." or "12-wks." or null if supply info is not available.
 * Uses autoship data from prescription or retailOrderLineItem.
 */
const getSupplyDuration = (
  rx: Prescription,
  autoshipUnit: Constant[],
): string | null => {
  const autoshipFrequency = getPrescriptionAutoshipFrequency(rx)
  const autoshipUnitId = getPrescriptionAutoshipUnitId(rx)

  if (!autoshipFrequency || !autoshipUnitId) {
    return null
  }

  const autoshipUnitObj = autoshipUnit.find((u) => u.id === autoshipUnitId)

  if (!autoshipUnitObj?.name) {
    return null
  }

  return formatSupplyDuration(autoshipFrequency, autoshipUnitObj.name)
}

interface QuantityRequestedOptions {
  autoshipUnit: Constant[]
  prescriptionProductSizeUnit: Constant[]
}

/**
 * Formats quantity and refills text matching QuantityAndRefills component logic.
 * Used for the "Qty Requested" field in declined/pending vet approval prescriptions.
 */
const getQuantityRequested = (
  rx: Prescription,
  { autoshipUnit, prescriptionProductSizeUnit }: QuantityRequestedOptions,
): string => {
  const quantity = rx.totalQuantity ?? getHighValue(rx.quantity) ?? 0

  const unitOfProduct = prescriptionProductSizeUnit.find(
    (unit) => unit.id === rx.totalQuantityUnitId,
  )

  const quantityText = unitOfProduct?.name
    ? `${quantity} ${unitOfProduct.name}`
    : String(quantity)

  const supplyDuration = getSupplyDuration(rx, autoshipUnit)
  const supplyText = supplyDuration ? ` (${supplyDuration} supply)` : ''

  const refillSeparator = ', '
  const { quantityNumbRefills } = rx
  const refillText =
    quantityNumbRefills && quantityNumbRefills > 0
      ? `${refillSeparator}${i18n.t('Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS', {
          number: quantityNumbRefills,
        })}`
      : `${refillSeparator}${i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.NO_REFILLS')}`

  return `${quantityText}${supplyText}${refillText}`
}

const getFulfillmentText = (prescriptionType: PrescriptionType): string => {
  switch (prescriptionType) {
    case PrescriptionType.CHEWY:
      return i18n.t('Common:CHEWY_COM')
    case PrescriptionType.IN_HOUSE:
      return i18n.t('Common:IN_HOUSE')
    case PrescriptionType.SCRIPT_OUT:
      return i18n.t('Common:OUTSIDE_PHARMACY')
    default:
      return ''
  }
}

const getLastFill = (rx: Prescription): string => {
  const lastFillDate = PrescriptionFields.lastFillDate(rx)
  return lastFillDate ? DateFormatters.date(lastFillDate) : ''
}

const buildFields = (
  conditionalFields: Array<DisplayField | null | false>,
): DisplayField[] => conditionalFields.filter(Boolean) as DisplayField[]

const getDeclinedFields = (
  rx: Prescription,
  prescriptionType: PrescriptionType | null | undefined,
  opts: GetRxFieldsOptions,
): DisplayField[] => {
  const { prescriptionProductSizeUnit = [], autoshipUnit = [] } = opts

  return buildFields([
    rx.creationDate
      ? {
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
          value: DateFormatters.dateTime(rx.creationDate),
        }
      : null,

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    prescriptionProductSizeUnit.length > 0
      ? {
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
          value: getQuantityRequested(rx, {
            autoshipUnit,
            prescriptionProductSizeUnit,
          }),
        }
      : null,
  ])
}

const getStandardFields = (
  rx: Prescription,
  prescriptionType: PrescriptionType | null | undefined,
  opts?: GetRxFieldsOptions,
): DisplayField[] => {
  const isScriptOut = prescriptionType === PrescriptionType.SCRIPT_OUT
  const isChewy = prescriptionType === PrescriptionType.CHEWY

  return buildFields([
    (() => {
      const expirationDate = PrescriptionFields.expirationDate(rx)
      if (!expirationDate && !isChewy) return null
      return {
        label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.EXPIRATION'),
        value: expirationDate ? DateFormatters.date(expirationDate) : '',
      }
    })(),

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    {
      label: i18n.t(
        'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
      ),
      value: toStringOrZero(PrescriptionFields.numberOfRefills(rx)),
    },

    !isScriptOut && {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.REFILLS_REMAINING'),
      value: toStringOrZero(PrescriptionFields.refillsRemaining(rx)),
      onClick: opts?.onDispenseRefill,
      tooltipText: opts?.onDispenseRefill
        ? i18n.t('Common:DISPENSE_REFILL')
        : undefined,
    },

    !isScriptOut && prescriptionType
      ? {
          boldValue: !getLastFill(rx),
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.LAST_FILL'),
          value:
            getLastFill(rx) ||
            i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.IN_PROGRESS'),
        }
      : null,
  ])
}

export const getRxFields = (
  rx: Prescription,
  prescriptionType: PrescriptionType | null | undefined,
  options: GetRxFieldsOptions = {},
): DisplayField[] => {
  const { prescriptionStates } = options

  const { isDeclined, isCanceled, isPendingVetApproval } = getPrescriptionFlags(
    rx,
    prescriptionType,
    prescriptionStates,
  )

  const showDeclinedFields = (isDeclined || isPendingVetApproval) && !isCanceled

  return showDeclinedFields
    ? getDeclinedFields(rx, prescriptionType, options)
    : getStandardFields(rx, prescriptionType, options)
}

export const getInstructionsField = (rx: Prescription): DisplayField => ({
  label: i18n.t('Common:INSTRUCTION_PRINTED_ON_LABEL'),
  value: rx.notes || '',
})

export const getPrescriptionStateName = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): string | null => getPrescriptionFlags(rx, null, prescriptionStates).stateName

/**
 * Checks if a prescription is a vet diet prescription.
 */
export const isVetDietPrescription = (prescription: Prescription): boolean =>
  prescription.rxType === RxType.VetDiet

/**
 * Checks if a field label matches the QTY_REQUESTED label.
 */
export const isQtyRequestedField = (
  fieldLabel: string,
  qtyRequestedLabel: string,
): boolean => fieldLabel === qtyRequestedLabel

/**
 * Checks if a prescription is completed based on filledBy or filledById.
 */
const isPrescriptionCompleted = (rx: Prescription): boolean =>
  Boolean(rx.filledBy ?? rx.filledById)

/**
 * Gets the fill date for a prescription.
 * Returns modificationDate || updatedAt || creationDate if completed, otherwise null.
 */
const getFillDate = (rx: Prescription): string | null => {
  if (isPrescriptionCompleted(rx)) {
    return rx.modificationDate || rx.updatedAt || rx.creationDate || null
  }
  return null
}

export const getOrderFillStatusText = (rx: Prescription): string => {
  if (isPrescriptionCompleted(rx)) {
    const fillDate = getFillDate(rx)
    const formattedDate = fillDate ? DateFormatters.date(fillDate) : ''
    return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_COMPLETED', {
      date: formattedDate,
    })
  }
  return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_TODO')
}

export const getOrderTableHeaderStatusText = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): string | null => {
  if (!rx.stateId || !prescriptionStates) return null

  const stateName =
    prescriptionStates.find((s) => s.id === rx.stateId)?.name ?? null
  return stateName || null
}

const PrescriptionStateToColorVariant: Record<string, ColorVariant> = {
  [PrescriptionStates.PRESCRIBED]: ColorVariant.SUCCESS,
  [PrescriptionStates.APPROVED]: ColorVariant.SUCCESS,
  [PrescriptionStates.CANCELED]: ColorVariant.ERROR,
  [PrescriptionStates.DECLINED]: ColorVariant.ERROR,
  [PrescriptionStates.PENDING_VET_APPROVAL]: ColorVariant.IMPORTANT,
}

export const getOrderTableHeaderStatusColor = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): KibTagThemes => {
  if (rx.stateId && prescriptionStates) {
    const stateName = prescriptionStates.find((s) => s.id === rx.stateId)?.name

    if (stateName && PrescriptionStateToColorVariant[stateName]) {
      return ColorVariantToTheme[PrescriptionStateToColorVariant[stateName]]
    }
  }

  return ColorVariantToTheme[ColorVariant.NORMAL]
}
